import { apiClient } from '@/lib/api-client';

/*
 * «Estrai e oscura» (#557, decision 158): the HTTP contract agreed between platform and tools on
 * #557 (comments 6160536529 and 6160560013), written by hand.
 *
 * TODO(#557 P2b): the shell's OpenAPI does not exist yet. When platform's P2b lands, replace the
 * types below and the `client` cast with the generated `paths` and `components`.
 */

/** The pipeline's type ids as the API spells them: hyphenated. */
export type TypeId =
  | 'person'
  | 'email'
  | 'phone'
  | 'address'
  | 'birth-date'
  | 'tax-code'
  | 'vat-number'
  | 'iban'
  | 'card'
  | 'identity-document'
  | 'health-card'
  | 'ip'
  | 'plate'
  | 'username';

export type RunLanguage = 'auto' | 'it' | 'en';

export interface RunOptions {
  readonly language: RunLanguage;
  readonly extraTerms: readonly string[];
  readonly keepTerms: readonly string[];
}

export type RunSource =
  { readonly kind: 'file'; readonly file: File } | { readonly kind: 'url'; readonly url: string };

/** `200` body of `POST /v1/clean-text/runs`. Never carries an original value (decision 5). */
export interface CleanTextSummary {
  readonly inputKind: string;
  readonly language: 'it' | 'en';
  /** `null` for formats without pages. */
  readonly pages: number | null;
  readonly pagesNotRead: number;
  readonly countsByType: Readonly<Record<string, number>>;
  /** Count per type id: well-shaped codes that failed their check, still replaced. */
  readonly lowConfidence?: Readonly<Record<string, number>>;
  /** Placeholders of bare surnames left apart because two people share them. */
  readonly ambiguous?: readonly string[];
  /** Codes such as `fallback_converter`. */
  readonly warnings?: readonly string[];
  readonly durationMs: number;
}

export interface CleanTextResult {
  readonly markdown: string;
  readonly summary: CleanTextSummary;
}

export type RefusalCode =
  | 'INVALID_REQUEST'
  | 'ACTIVE_CONTEXT_REQUIRED'
  | 'UNAUTHENTICATED'
  | 'PERMISSION_DENIED'
  | 'MODULE_NOT_ENTITLED'
  | 'DOCUMENT_TOO_LARGE'
  | 'UNSUPPORTED_TYPE'
  | 'DOCUMENT_UNREADABLE'
  | 'URL_REFUSED'
  | 'URL_FETCH_FAILED'
  | 'AUTHORIZATION_UNAVAILABLE'
  | 'TIMED_OUT';

/** A problem-details body with the typed `code` (decision 158). */
export interface Refusal {
  readonly status: number;
  readonly code?: string;
  /** `TIMED_OUT` on an instance that had just started. */
  readonly retryable?: boolean;
  /** `DOCUMENT_TOO_LARGE`: which limit was hit. */
  readonly exceeded?: 'bytes' | 'pages' | 'characters' | 'cells';
  readonly limits?: {
    readonly bytes?: number;
    readonly pages?: number;
    readonly characters?: number;
    readonly cells?: number;
  };
}

export type RunOutcome =
  | { readonly kind: 'ok'; readonly result: CleanTextResult }
  | { readonly kind: 'refused'; readonly refusal: Refusal }
  | { readonly kind: 'aborted' }
  | { readonly kind: 'network' };

/** 5 MB, checked in the browser before the upload and again by the server. */
export const MAX_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_TERMS = 50;
export const MAX_TERM_LENGTH = 100;

interface ClientResult {
  readonly data?: unknown;
  readonly error?: unknown;
  readonly response: Response;
}

/**
 * The shared client, which adds the bearer token and the `X-Active-*` headers to every request,
 * seen through the two routes of this contract.
 */
interface CleanTextClient {
  POST(
    path: '/v1/clean-text/runs',
    init: {
      body: FormData;
      bodySerializer: (body: FormData) => FormData;
      parseAs: 'text';
      signal?: AbortSignal | undefined;
    },
  ): Promise<ClientResult>;
  GET(path: '/v1/clean-text/warm-up', init?: { signal?: AbortSignal }): Promise<ClientResult>;
}

const client = apiClient as unknown as CleanTextClient;

/**
 * Wakes the service (devops' point a): no authentication, `204`. Called once when the screen opens;
 * the caller never waits on it to send. Resolves whatever happens, because a cold instance that
 * does not answer must not hold the button.
 */
export async function warmUp(): Promise<void> {
  try {
    await client.GET('/v1/clean-text/warm-up');
  } catch {
    // Nothing to tell the user: the run itself reports what failed.
  }
}

/** One multipart request, answered with JSON (no streaming in v1: Hosting cuts at 60 s). */
export async function runCleanText(
  source: RunSource,
  options: RunOptions,
  signal?: AbortSignal,
): Promise<RunOutcome> {
  const form = new FormData();
  if (source.kind === 'file') form.append('file', source.file, source.file.name);
  else form.append('url', source.url);
  if (options.language !== 'auto') form.append('language', options.language);
  for (const term of options.extraTerms) form.append('extraTerms', term);
  for (const term of options.keepTerms) form.append('keepTerms', term);

  try {
    const { data, error, response } = await client.POST('/v1/clean-text/runs', {
      body: form,
      bodySerializer: (body) => body,
      parseAs: 'text',
      signal,
    });
    if (response.ok) {
      const result = parseJson<CleanTextResult>(data);
      if (result && typeof result.markdown === 'string' && result.summary) {
        return { kind: 'ok', result };
      }
      return { kind: 'refused', refusal: { status: response.status } };
    }
    const problem = parseJson<Omit<Refusal, 'status'>>(error) ?? {};
    return { kind: 'refused', refusal: { ...problem, status: response.status } };
  } catch (caught) {
    if (signal?.aborted || (caught instanceof DOMException && caught.name === 'AbortError')) {
      return { kind: 'aborted' };
    }
    return { kind: 'network' };
  }
}

function parseJson<T>(value: unknown): T | undefined {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return undefined;
    }
  }
  return typeof value === 'object' && value !== null ? (value as T) : undefined;
}

/** `https` only: the fetcher refuses `http` and any other port (#565). */
export function isHttpsAddress(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' && url.hostname.includes('.');
  } catch {
    return false;
  }
}
